/**
 * Emit hosts for the `patient` analytics group — 14 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import {
  resolveSessionsCrossClinicianLink,
  withPatientSharedProps,
  type PatientSharedProfileSummary,
} from '@/lib/analytics/patient-shared-event-props';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  type PatientContextToggleOffEventProps,
  type PatientContextToggleOnEventProps,
  type PatientCreatedEventProps,
  type PatientExtractionModalOpenedEventProps,
  type PatientExtractionReviewedEventProps,
  type PatientLinkedEventProps,
  type PatientSelectedEventProps,
  type PatientSummaryCtaShownEventProps,
  type PatientSummaryEmailSendAttemptedEventProps,
  type PatientSummaryEmailSentEventProps,
  type PatientSummarySmsSendAttemptedEventProps,
  type PatientSummarySmsSentEventProps,
  type PatientUnlinkedEventProps,
  type PatientUpdatedEventProps,
  type PatientViewedEventProps,
} from '@/models/analytics/features/patient';
import { type SessionTeamContext } from '@heidi/session/contracts';

export function trackPatientContextToggleOff(input: PatientContextToggleOffEventProps) {
  track({
    event_name: TrackingEventName.PatientContextToggleOff,
    props: input,
  });
}

export function trackPatientContextToggleOn(input: PatientContextToggleOnEventProps) {
  track({
    event_name: TrackingEventName.PatientContextToggleOn,
    props: withPatientSharedProps(input),
  });
}

export function trackPatientExtractionModalOpened(
  input: PatientExtractionModalOpenedEventProps
) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ExtractionModalOpened,
    props: input,
  });
}

export function trackPatientExtractionReviewed(
  input: PatientExtractionReviewedEventProps
) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ExtractionReviewed,
    props: input,
  });
}

// Patient linking events
/**
 * `profile` is what the caller already holds about the patient (manager,
 * backend count); without it the shared properties fall back to the by-id
 * profile cache. `is_cross_clinician_link` is resolved here from the sessions'
 * owners unless the caller set it. The team-member (review mode) paths pass
 * `teamContext` so those owners are read from the member-scoped session cache.
 */
export function trackPatientLinked(
  input: PatientLinkedEventProps,
  profile?: PatientSharedProfileSummary | null,
  teamContext?: SessionTeamContext
) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PatientLinked,
    props: {
      is_cross_clinician_link: resolveSessionsCrossClinicianLink(
        input.session_ids,
        teamContext
      ),
      ...withPatientSharedProps(input, profile),
    },
  });
}

/**
 * `patient_created` (COLAB-422 moved the two inline `track` calls in
 * `useMutateCreatePatientProfile` here so the shared properties stamp in one
 * place). `profile` is the created profile from the response.
 */
export function trackPatientCreated(
  input: PatientCreatedEventProps,
  profile?: PatientSharedProfileSummary | null
) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PatientCreated,
    props: withPatientSharedProps(input, profile),
  });
}

export function trackPatientSelected(input: PatientSelectedEventProps) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PatientSelected,
    props: withPatientSharedProps(input),
  });
}

export function trackPatientSummaryCtaShown(input: PatientSummaryCtaShownEventProps) {
  track({
    event_name: TrackingEventName.PatientSummaryCtaShown,
    props: input,
    options: { amplitude: false },
  });
}

export function trackPatientSummaryEmailSendAttempted(
  input: PatientSummaryEmailSendAttemptedEventProps
) {
  track({
    event_name: TrackingEventName.PatientSummaryEmailSendAttempted,
    props: input,
    options: { amplitude: false },
  });
}

export function trackPatientSummaryEmailSent(input: PatientSummaryEmailSentEventProps) {
  track({
    event_name: TrackingEventName.PatientSummaryEmailSent,
    props: input,
    options: { amplitude: false },
  });
}

/**
 * Total patient-summary SMS send volume — fired when the clinician
 * confirms the recipient and clicks Send, before the dispatch result.
 * Routes to Amplitude + Datadog + Snowplow; pair with
 * `trackPatientSummarySmsSent` to derive the success rate.
 */
export function trackPatientSummarySmsSendAttempted(
  input: PatientSummarySmsSendAttemptedEventProps
) {
  track({
    event_name: TrackingEventName.PatientSummarySmsSendAttempted,
    props: input,
  });
}

/**
 * Successful patient-summary SMS dispatch (Twilio accepted). Routes to
 * Amplitude + Datadog + Snowplow so send volume / success rate can be
 * tracked in Amplitude alongside the existing destinations.
 */
export function trackPatientSummarySmsSent(input: PatientSummarySmsSentEventProps) {
  track({
    event_name: TrackingEventName.PatientSummarySmsSent,
    props: input,
  });
}

export function trackPatientUnlinked(
  input: PatientUnlinkedEventProps,
  profile?: PatientSharedProfileSummary | null,
  teamContext?: SessionTeamContext
) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PatientUnlinked,
    props: {
      is_cross_clinician_link: resolveSessionsCrossClinicianLink(
        input.session_ids,
        teamContext
      ),
      ...withPatientSharedProps(input, profile),
    },
  });
}

export function trackPatientUpdated(input: PatientUpdatedEventProps) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PatientUpdated,
    props: input,
  });
}

export function trackPatientViewed(input: PatientViewedEventProps) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PatientViewed,
    props: input,
  });
}
